import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { createContext, isValidElement, useContext } from 'react'
import type * as React from 'react'

import { cn } from '@/lib/utils'

/** Long enough that crossing a table does not strobe, short enough to feel like a hover response. */
const TOOLTIP_DELAY_MS = 300

/** Tags that already take focus. Anything else used as a trigger needs a tab stop of its own. */
const FOCUSABLE_TAGS = new Set(['a', 'button', 'input', 'select', 'textarea'])

const TRIGGER_FOCUS_CLASSES = 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'

/** True once a provider is above us, so `Tooltip` can stop mounting one per tooltip. */
const HasTooltipProvider = createContext(false)

function TooltipProvider({
	delayDuration = TOOLTIP_DELAY_MS,
	skipDelayDuration = TOOLTIP_DELAY_MS,
	...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
	return (
		<HasTooltipProvider.Provider value={true}>
			<TooltipPrimitive.Provider
				data-slot='tooltip-provider'
				delayDuration={delayDuration}
				skipDelayDuration={skipDelayDuration}
				{...props}
			/>
		</HasTooltipProvider.Provider>
	)
}

/**
 * Consumes the app-level provider when there is one, so its delay and skip window govern every
 * tooltip. A provider per tooltip would shadow both, and neighbouring triggers could never share a
 * skip window. The fallback keeps a bare `<Tooltip>` working in tests and isolated renders.
 */
function Tooltip({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
	const hasProvider = useContext(HasTooltipProvider)
	const root = <TooltipPrimitive.Root data-slot='tooltip' disableHoverableContent={true} {...props} />
	return hasProvider ? root : <TooltipProvider>{root}</TooltipProvider>
}

type TooltipTriggerProps = React.ComponentProps<typeof TooltipPrimitive.Trigger> & {
	/**
	 * Whether a trigger that cannot take focus on its own gets a tab stop. Pass `false` where the
	 * tooltip only repeats text already in the DOM (a truncated cell), so a long table does not turn
	 * into a hundred tab stops.
	 */
	focusable?: boolean
}

/**
 * Two fixes the primitive owes every call site.
 *
 * A disabled control receives no pointer events (`disabled:pointer-events-none` on Button, and
 * browsers suppress them on disabled form controls regardless), so a tooltip anchored straight to it
 * can never open. That is exactly the tooltip explaining why the control is disabled, so wrap it.
 *
 * A span or div trigger takes no focus, which leaves its tooltip unreachable without a mouse.
 */
function TooltipTrigger({ focusable = true, children, ...props }: TooltipTriggerProps) {
	const child = props.asChild && isValidElement(children) ? children : null
	const childProps = (child?.props ?? {}) as { disabled?: boolean; tabIndex?: number; className?: string }

	if (child && childProps.disabled) {
		return (
			<TooltipPrimitive.Trigger data-slot='tooltip-trigger' {...props}>
				<span className={cn('inline-flex', focusable && TRIGGER_FOCUS_CLASSES)} tabIndex={focusable ? 0 : undefined}>
					{children}
				</span>
			</TooltipPrimitive.Trigger>
		)
	}

	const needsTabStop =
		focusable &&
		child &&
		typeof child.type === 'string' &&
		!FOCUSABLE_TAGS.has(child.type) &&
		childProps.tabIndex == null

	return (
		<TooltipPrimitive.Trigger
			data-slot='tooltip-trigger'
			{...(needsTabStop ? { tabIndex: 0, className: TRIGGER_FOCUS_CLASSES } : {})}
			{...props}>
			{children}
		</TooltipPrimitive.Trigger>
	)
}

function TooltipContent({
	className,
	// Keep content + arrow clear of the trigger: at 0 the content spawns under a cursor
	// entering from the tooltip side, firing pointerleave on the trigger and closing it.
	sideOffset = 6,
	hideArrow = false,
	children,
	...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> & { hideArrow?: boolean }) {
	return (
		<TooltipPrimitive.Portal>
			<TooltipPrimitive.Content
				data-slot='tooltip-content'
				sideOffset={sideOffset}
				className={cn(
					'bg-tooltip text-tooltip-foreground rounded-md shadow-tooltip px-3 py-1.5 text-control text-balance',
					// No slide-in-from-* here: the slide starts the content 8px toward the trigger,
					// overlapping a cursor entering from the tooltip side and closing it mid-animation.
					'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
					// A tooltip explains what is inside dialogs, sheets and popovers, so it sits above all of them.
					'z-(--z-tooltip) w-fit max-w-xs break-words origin-(--radix-tooltip-content-transform-origin)',
					className,
				)}
				{...props}>
				{children}
				{!hideArrow && (
					<TooltipPrimitive.Arrow className='bg-tooltip fill-tooltip z-(--z-tooltip) size-2.5 translate-y-[calc(-50%-0.125rem)] rotate-45 rounded-none' />
				)}
			</TooltipPrimitive.Content>
		</TooltipPrimitive.Portal>
	)
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
